import { forwardRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  IconAlignLeft,
  IconCustomExport,
  IconDeviceFloppy,
  IconFile,
  IconPlayerPlay,
  IconRefresh,
} from '@rossum/ui/icons/tabler';
import {
  Button,
  ButtonProps,
  CircularProgress,
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
} from '@rossum/ui/material';
import { EventNameOptions } from '../types';
import { FunctionEventsDropdown } from './FunctionEventsDropdown';

const InputBoxButton = forwardRef<HTMLDivElement, ButtonProps>(
  ({ children, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        component="div"
        size="small"
        variant="contained"
        color="inherit"
        sx={{
          backgroundColor: t => t.palette.action.selected,
          minWidth: 40,
          '&:hover': {
            backgroundColor: t => t.palette.action.hover,
          },
        }}
        {...props}
      >
        {children}
      </Button>
    );
  }
);

type Props = {
  hasError: boolean;
  isRunning: boolean;
  functionEvents: Array<EventNameOptions>;
  assignedFunctionEventNames: Array<EventNameOptions>;
  currentEvent: EventNameOptions;
  currentObject: string;
  onPrettify: () => void;
  onRun: () => void;
  onSave: () => void;
  onLoad: () => void;
  canRun: boolean;
  canSave: boolean;
  savedInput: string;
  setCurrentEvent: (_event: EventNameOptions) => void;
  setCurrentObject: (_objectRef: string) => void;
  setShowInputLoading: (loading: boolean) => void;
  isSaving?: boolean;
};

export const InputBoxMenu = ({
  assignedFunctionEventNames,
  canRun,
  canSave,
  currentEvent,
  currentObject,
  functionEvents,
  hasError,
  isRunning,
  onPrettify,
  onRun,
  onLoad,
  onSave,
  savedInput,
  setCurrentEvent,
  setCurrentObject,
  setShowInputLoading,
  isSaving,
}: Props) => {
  const intl = useIntl();
  const unsavedChanges = canSave && currentEvent !== 'mySavedInput';
  return (
    <Stack sx={{ backgroundColor: 'background.paper' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 0.5,
          borderTop: t => `1px solid ${t.palette.divider}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} useFlexGap>
          <SvgIcon fontSize="medium" color="action">
            <IconCustomExport />
          </SvgIcon>
          <FormattedMessage id="components.editor.inputBox.title" />
        </Stack>
        <Stack direction="row" spacing={1} alignContent="center">
          <Tooltip
            title={intl.formatMessage({
              id: unsavedChanges
                ? 'components.editor.inputBox.dropdown.unsavedChanges'
                : 'components.editor.inputBox.dropdown.event',
            })}
            placement="top"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <FunctionEventsDropdown
                assignedFunctionEventNames={assignedFunctionEventNames}
                currentEvent={currentEvent}
                setCurrentEvent={event => {
                  setShowInputLoading(true);
                  setCurrentEvent(event);
                }}
                savedInput={savedInput}
                events={functionEvents}
                disabled={unsavedChanges}
              />
            </Stack>
          </Tooltip>

          <Tooltip
            placement="top"
            title={intl.formatMessage({
              id: 'components.editor.inputBox.prettify',
            })}
          >
            <InputBoxButton
              onClick={onPrettify}
              disabled={isRunning || hasError}
              data-cy="prettify-button"
            >
              <SvgIcon fontSize="medium" color="action">
                <IconAlignLeft />
              </SvgIcon>
            </InputBoxButton>
          </Tooltip>
          <Tooltip
            title={intl.formatMessage({
              id: 'components.editor.inputBox.run',
            })}
            placement="top"
          >
            <InputBoxButton
              onClick={onRun}
              disabled={!canRun || hasError}
              data-cy="run-button"
            >
              {isRunning ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SvgIcon fontSize="medium">
                  <IconPlayerPlay />
                </SvgIcon>
              )}
            </InputBoxButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={1}
        sx={{
          px: 2,
          py: 0.5,
          borderTop: t => `1px solid ${t.palette.divider}`,
        }}
      >
        <Stack direction="row" alignItems="center" flex={1} spacing={1}>
          <SvgIcon fontSize="medium" color="action">
            <IconFile />
          </SvgIcon>
          {currentEvent === 'invocation' ||
          currentEvent === 'mySavedInput' ? null : (
            <Stack
              sx={{ width: '100%' }}
              alignItems="center"
              direction="row"
              spacing={1}
            >
              <TextField
                placeholder={intl.formatMessage({
                  id: `components.editor.inputBox.objectInput.placeholder.${currentEvent}`,
                })}
                value={currentObject}
                onChange={newValue => setCurrentObject(newValue.target.value)}
                size="small"
                margin="dense"
                inputProps={{
                  'data-cy': 'extensions-editor-object-input',
                  sx: { fontSize: '13px', padding: '4px 14px' },
                }}
                sx={{ margin: '0 0', width: '100%' }}
              />
              <InputBoxButton onClick={onLoad} disabled={!currentObject}>
                <SvgIcon fontSize="medium">
                  <IconRefresh />
                </SvgIcon>
              </InputBoxButton>
            </Stack>
          )}
        </Stack>
        <Tooltip
          placement="top"
          title={intl.formatMessage({
            id: canSave
              ? 'components.editor.inputBox.save'
              : hasError
                ? 'components.editor.inputBox.hasError'
                : 'components.editor.inputBox.noChangesToSave',
          })}
        >
          <InputBoxButton
            onClick={onSave}
            disabled={!canSave || hasError}
            data-cy="save-button"
          >
            {isSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SvgIcon fontSize="medium">
                <IconDeviceFloppy />
              </SvgIcon>
            )}
          </InputBoxButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
